import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Lightbulb,
  MessageSquare,
  Users,
  TrendingUp,
  Calendar,
  BarChart3,
  ExternalLink,
  Activity,
  Eye,
  Target,
} from "lucide-react";

interface CampaignActivityModalProps {
  campaign: {
    id: string;
    title: string;
    creator: string;
    responses: number;
    comments: number;
    engagementRate: number;
    validationScore: number;
    status: string;
    daysActive: number;
  } | null;
  isOpen: boolean;
  onClose: () => void;
}

const CampaignActivityModal: React.FC<CampaignActivityModalProps> = ({
  campaign,
  isOpen,
  onClose,
}) => {
  if (!campaign) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-accent to-primary flex items-center justify-center text-white">
              <Lightbulb className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xl font-bold">{campaign.title}</div>
              <div className="text-sm text-muted-foreground">
                by {campaign.creator}
              </div>
            </div>
          </DialogTitle>
          <DialogDescription>
            Detailed campaign performance and engagement overview
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Campaign Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Total Responses
                </span>
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {campaign.responses}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  Comments
                </span>
              </div>
              <div className="text-2xl font-bold text-green-900">
                {campaign.comments}
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">
                  Engagement Rate
                </span>
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {campaign.engagementRate}%
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  Validation Score
                </span>
              </div>
              <div className="text-2xl font-bold text-orange-900">
                {campaign.validationScore}%
              </div>
            </div>
          </div>

          {/* Campaign Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                Campaign Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">
                    Status
                  </span>
                  <Badge
                    variant={
                      campaign.status === "ACTIVE"
                        ? "default"
                        : campaign.status === "ARCHIVED"
                        ? "secondary"
                        : "outline"
                    }
                  >
                    {campaign.status}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">
                    Days Active
                  </span>
                  <span className="text-sm font-semibold">
                    {campaign.daysActive} days
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium text-muted-foreground">
                    Creator
                  </span>
                  <span className="text-sm font-semibold">
                    {campaign.creator}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Activity
              </h3>
              <div className="space-y-3">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">Latest Response</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    New feedback received - 2 hours ago
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium">Comment Posted</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    User engagement increased - 1 day ago
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium">
                      Performance Boost
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Engagement rate improved - 3 days ago
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Chart Placeholder */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Performance Trends
            </h3>
            <div className="h-48 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200 flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">
                  Performance chart would be displayed here
                </p>
              </div>
            </div>
          </div>

          {/* Validation Progress */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5" />
              Validation Progress
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Overall Score</span>
                <span className="text-sm font-semibold">
                  {campaign.validationScore}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${campaign.validationScore}%` }}
                ></div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">85%</div>
                  <div className="text-xs text-green-600">Quality</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">92%</div>
                  <div className="text-xs text-blue-600">Engagement</div>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <div className="text-lg font-bold text-purple-600">78%</div>
                  <div className="text-xs text-purple-600">Relevance</div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                window.open(`/campaign/${campaign.id}`, "_blank");
              }}
              className="flex-1"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Campaign
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                window.open(`/admin/moderation`, "_blank");
              }}
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-2" />
              Manage Campaign
            </Button>
            <Button onClick={onClose} className="flex-1">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignActivityModal;
